import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { t } from '../i18n.js';
import { formatDateTime, formatSize, initials } from '../format.js';
import {
  adminBusy,
  adminStats,
  adminUsers,
  backlog,
  loadAdminConsole,
  onDescribeBacklog,
  onVisualHashBacklog,
  visualHashProgress,
  onCreateAccount,
  onResetAccountPassword,
  onSetAccountStatus,
  sudoNeeded,
  reauthenticate,
} from '../actions.js';
import { me } from '../state.js';
import { Action, Empty, Pill } from './parts.js';

/**
 * **La console d'administration** — l'écran qui manquait, et dont l'absence a
 * coûté 966 comptes.
 *
 * ### Ce qu'elle répare
 *
 * Le socle savait créer un compte depuis l'étape 1 (`POST /admin/users`, D34) et
 * **aucun client ne l'exposait** : le seul chemin praticable était le harnais de
 * test ou un script. Aucune route ne permettait non plus d'**énumérer** les
 * comptes, si bien que rien ne comptait et rien n'alertait — 966 comptes de test
 * se sont accumulés sur la pile de développement sans que personne ne puisse s'en
 * apercevoir autrement qu'en allant lire le pool Cognito à la main.
 *
 * La leçon n'est pas « il manquait un écran » : c'est qu'**un système sans
 * compteur ne peut être surveillé par personne**, et que la capacité de créer
 * sans la capacité de compter produit exactement ce résultat.
 *
 * ### Deux familles de chiffres, et pourquoi elles ne se mélangent pas
 *
 * Les **objets métier** sont exacts : ils viennent des compteurs que chaque profil
 * tient transactionnellement, sommés à la lecture. Les **objets techniques** sont
 * approchés — `ItemCount` d'une table est rafraîchi toutes les six heures, les
 * métriques S3 sont quotidiennes, la profondeur d'une file est approximative par
 * construction. Les présenter côte à côte sans le dire ferait conclure à une fuite
 * au premier écart, alors que l'écart est l'âge de la mesure. Chaque valeur
 * technique porte donc sa date.
 *
 * ### L'écart entre les deux mondes est le chiffre qui compte
 *
 * `comptes en base` en face de `comptes Cognito` : c'est précisément la
 * différence qui était invisible, et c'est elle qu'on regarde en premier.
 */
export function AdminView(): JSX.Element | null {
  const profile = me.value;
  const [tab, setTab] = useState<'dashboard' | 'users'>('dashboard');

  useEffect(() => {
    void loadAdminConsole();
  }, []);

  // La garde est **serveur** — `assertAdmin` relit `PROFILE.Role` à chaque appel.
  // Celle-ci n'évite qu'un écran vide et une paire de 403 : elle ne protège rien,
  // et ne doit pas être confondue avec une autorisation.
  if (!profile?.isAdmin) return null;

  return (
    // **Pleine largeur** : un tableau de bord se compare du regard, et la mesure
    // de lecture — qui vaut pour un écran de réglages — y laissait la moitié
    // droite vide en poussant huit compteurs dans quarante-six rems.
    <div class="stack wide" data-testid="admin">
      {sudoNeeded.value ? <SudoBanner /> : null}

      <div class="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class={tab === 'dashboard' ? 'tab current' : 'tab'}
          aria-selected={tab === 'dashboard'}
          data-testid="tab-dashboard"
          onClick={() => setTab('dashboard')}
        >
          {t('admin.dashboard')}
        </button>
        <button
          type="button"
          role="tab"
          class={tab === 'users' ? 'tab current' : 'tab'}
          aria-selected={tab === 'users'}
          data-testid="tab-accounts"
          onClick={() => setTab('users')}
        >
          {t('admin.accounts')}
        </button>

        <span class="grow" />
        {/* **Un tableau de bord se relit.** Sans ce bouton, la seule façon de
            revoir les chiffres après une opération était de recharger la page —
            donc de refaire une session, un `GET /me` et une synchronisation
            complète pour lire deux compteurs. L'heure affichée est celle de la
            **lecture**, qui est vraie ; l'âge des mesures elles-mêmes est porté
            par chaque case, et ce n'est pas la même chose. */}
        {adminStats.value ? (
          <span class="card-meta" data-testid="admin-read-at">
            {t('admin.readAt', { at: formatDateTime(adminStats.value.at) })}
          </span>
        ) : null}
        <Action testId="admin-refresh" onClick={() => void loadAdminConsole()}>
          {adminBusy.value ? t('admin.refreshing') : t('admin.refresh')}
        </Action>
      </div>

      {tab === 'dashboard' ? <Dashboard /> : <Accounts />}
    </div>
  );
}

/**
 * **Le sudo, expliqué au moment où il sert.**
 *
 * D21 veut qu'un acte d'admin prouve une saisie de mot de passe récente. Plutôt
 * qu'un second client Cognito et une page de mot de passe maison — qu'un client
 * public ne doit jamais présenter —, on renvoie sur le managed login avec
 * `prompt=login`, que Cognito honore en exigeant une ré-authentification même en
 * présence d'une session.
 */
function SudoBanner(): JSX.Element {
  return (
    <p class="notice warning" role="status" data-testid="sudo-required">
      <span class="grow">{t('admin.sudoNeeded')}</span>
      <Action testId="sudo-reauth" onClick={() => void reauthenticate()}>
        {t('admin.reauthenticate')}
      </Action>
    </p>
  );
}

function Dashboard(): JSX.Element {
  const s = adminStats.value;
  if (!s) {
    return adminBusy.value ? (
      <p class="notice busy" role="status">
        <span class="spinner" aria-hidden="true" />
      </p>
    ) : (
      <Empty>{t('admin.noStats')}</Empty>
    );
  }

  const b = s.business;
  const k = s.technical;

  // **Le chiffre qu'on regarde en premier.** Un profil sans compte Cognito est un
  // espace inerte ; un compte Cognito sans profil est un accès qui ne mène nulle
  // part. Les deux se voient ici, et nulle part ailleurs.
  const ecart = k.cognitoUsers !== null ? k.cognitoUsers - b.users : null;

  // `wide` aussi ici : la pile racine la portait déjà, mais une pile **imbriquée**
  // réappliquait la mesure de lecture et refermait le tableau de bord sur 46 rem.
  return (
    <div class="stack wide">
      <section class="panel" data-testid="stats-business">
        <h3>{t('admin.business')}</h3>
        <div class="stat-grid">
          <Stat testId="stat-users" label={t('admin.users')} value={String(b.users)} />
          <Stat testId="stat-active" label={t('admin.active')} value={String(b.active)} />
          <Stat testId="stat-disabled" label={t('admin.disabled')} value={String(b.disabled)} />
          <Stat testId="stat-admins" label={t('admin.admins')} value={String(b.admins)} />
          <Stat testId="stat-entities" label={t('admin.entities')} value={String(b.entities)} />
          <Stat testId="stat-albums" label={t('admin.collections')} value={String(b.albums)} />
          <Stat testId="stat-groups" label={t('admin.groups')} value={String(b.groups)} />
          <Stat testId="stat-bytes" label={t('admin.stored')} value={formatSize(b.bytes)} />
        </div>
        {b.incomplete > 0 ? (
          // Une création interrompue entre la table et Cognito laisse un espace
          // **inerte** — personne ne peut s'y authentifier. Ce n'est pas une
          // erreur silencieuse : c'est un état que la route sait reprendre, et
          // qu'il faut donc voir pour la relancer.
          <p class="card-meta" data-testid="stat-incomplete">
            {t('admin.incomplete', { count: b.incomplete })}
          </p>
        ) : null}
      </section>

      <section class="panel" data-testid="stats-technical">
        <h3>{t('admin.technical')}</h3>
        {/* **La phrase qui empêche une fausse conclusion.** Sans elle, un écart
            entre le métier et la table se lit comme une fuite, alors que c'est
            l'âge de la mesure. */}
        <p class="card-meta">{t('admin.approx')}</p>
        <div class="stat-grid">
          <Stat
            testId="stat-cognito"
            label={t('admin.cognitoUsers')}
            value={k.cognitoUsers === null ? '—' : String(k.cognitoUsers)}
            hint={
              ecart === null || ecart === 0 ? undefined : t('admin.drift', { count: String(ecart) })
            }
          />
          <MesureStat testId="stat-items" label={t('admin.tableItems')} m={k.tableItems} />
          <MesureStat
            testId="stat-table-bytes"
            label={t('admin.tableBytes')}
            m={k.tableBytes}
            format={formatSize}
          />
          <MesureStat testId="stat-objects" label={t('admin.bucketObjects')} m={k.bucketObjects} />
          <MesureStat
            testId="stat-bucket-bytes"
            label={t('admin.bucketBytes')}
            m={k.bucketBytes}
            format={formatSize}
          />
          <Stat
            testId="stat-queue"
            label={t('admin.queue')}
            value={k.queueDepth === null ? '—' : String(k.queueDepth)}
            hint={k.queueInFlight ? t('admin.inFlight', { count: String(k.queueInFlight) }) : undefined}
          />
        </div>
      </section>

      {me.value?.features?.describe ? <DescribeBacklogPanel /> : null}
      <VisualHashBacklogPanel />
    </div>
  );
}

/**
 * **Décrire les photos déjà envoyées** *(R5, 2026-09-13)*.
 *
 * Deux gestes, et l'ordre compte : **compter** d'abord, qui ne coûte qu'un parcours
 * de la table, puis **enfiler**, qui coûte un appel au modèle par photo. Le premier
 * répond à la question qu'on se pose avant d'appuyer sur le second.
 *
 * La mise à niveau d'un prompt plus ancien est une case **décochée** : elle
 * réécrit ce que la machine tient déjà, et ce n'est pas le rattrapage ordinaire. Ce
 * qu'un utilisateur a corrigé n'est jamais touché, cochée ou non — la Lambda relit
 * chaque photo et applique la règle de remplissage.
 */
function DescribeBacklogPanel(): JSX.Element {
  const [upgrade, setUpgrade] = useState(false);
  const state = backlog.value;
  const shown = state && state.upgrade === upgrade ? state : undefined;

  return (
    <section class="panel" data-testid="describe-backlog">
      <h3>{t('admin.describeTitle')}</h3>
      <p class="card-meta">{t('admin.describeHelp')}</p>
      <label class="field inline">
        <input
          type="checkbox"
          data-testid="describe-upgrade"
          checked={upgrade}
          onChange={(e) => setUpgrade((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>{t('admin.describeUpgrade')}</span>
      </label>
      <div class="inspector-actions">
        <Action testId="describe-count" onClick={() => void onDescribeBacklog({ dryRun: true, upgrade })}>
          {t('admin.describeCount')}
        </Action>
        <Action
          testId="describe-run"
          variant="primary"
          onClick={() => void onDescribeBacklog({ dryRun: false, upgrade })}
        >
          {shown && !shown.dryRun && !shown.done ? t('admin.describeResume') : t('admin.describeRun')}
        </Action>
      </div>
      {shown ? (
        <p class="card-meta" data-testid="describe-result" role="status">
          {shown.dryRun
            ? t('admin.describeCounted', { selected: shown.selected, scanned: shown.scanned })
            : t('admin.describeQueued', { enqueued: shown.enqueued, scanned: shown.scanned })}
          {shown.done ? '' : ` ${t('admin.describeUnfinished')}`}
        </p>
      ) : null}
    </section>
  );
}

/**
 * **Calculer l'empreinte visuelle des photos déjà envoyées** *(K3)* — sans quoi
 * elles ne peuvent pas être comparées, et aucun doublon probable ne s'y signale.
 * Compter d'abord, enfiler ensuite, comme les descriptions.
 */
function VisualHashBacklogPanel(): JSX.Element {
  const shown = visualHashProgress.value;
  return (
    <section class="panel" data-testid="visual-hash-backlog">
      <h3>{t('admin.visualHashTitle')}</h3>
      <p class="card-meta">{t('admin.visualHashHelp')}</p>
      <div class="inspector-actions">
        <Action testId="visual-hash-count" onClick={() => void onVisualHashBacklog(true)}>
          {t('admin.describeCount')}
        </Action>
        <Action testId="visual-hash-run" variant="primary" onClick={() => void onVisualHashBacklog(false)}>
          {shown && !shown.dryRun && !shown.done ? t('admin.describeResume') : t('admin.visualHashRun')}
        </Action>
      </div>
      {shown ? (
        <p class="card-meta" data-testid="visual-hash-result" role="status">
          {shown.dryRun
            ? t('admin.visualHashCounted', { selected: shown.selected, scanned: shown.scanned })
            : t('admin.visualHashQueued', { enqueued: shown.enqueued, scanned: shown.scanned })}
          {shown.done ? '' : ` ${t('admin.describeUnfinished')}`}
        </p>
      ) : null}
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div class="stat" data-testid={testId}>
      <span class="stat-value">{value}</span>
      <span class="stat-label">{label}</span>
      {hint ? <span class="stat-hint">{hint}</span> : null}
    </div>
  );
}

/** Une mesure technique **avec sa date** : la valeur seule serait trompeuse. */
function MesureStat({
  label,
  m,
  format,
  testId,
}: {
  readonly label: string;
  readonly m: { value: number | null; at: string | null };
  readonly format?: (n: number) => string;
  readonly testId: string;
}): JSX.Element {
  const value = m.value === null ? '—' : format ? format(m.value) : String(Math.round(m.value));
  // **Pas de date inventée quand la source n'en donne pas.** `DescribeTable` ne
  // dit pas quand il a compté ; afficher l'heure de lecture faisait croire à une
  // mesure fraîche sur une valeur vieille de plusieurs heures — visible dès la
  // première purge, où l'écran annonçait 24 664 items sur une table qui n'en
  // portait plus sept. On dit alors ce qu'on sait : le décalage existe, sans
  // prétendre le dater.
  const hint =
    m.value === null ? undefined : m.at ? t('admin.measuredAt', { at: formatDateTime(m.at) }) : t('admin.lagging');
  return <Stat testId={testId} label={label} value={value} hint={hint} />;
}

function Accounts(): JSX.Element {
  const users = adminUsers.value;

  return (
    <div class="stack wide">
      <NewAccount />
      <section class="panel">
        <h3>{t('admin.accounts')}</h3>
        {users.length === 0 ? (
          <Empty>{t('admin.noAccounts')}</Empty>
        ) : (
          <ul class="rows" data-testid="accounts">
            {users.map((u) => (
              // **Trois informations, trois lignes.** Elles étaient trois `<span>`
              // dans un `<div class="grow">` — et `.grow` n'était mis en colonne
              // que sous `.items`. Sous `.rows`, ils restaient donc en ligne et se
              // collaient les uns aux autres : « Camille01JC0CAMILLEVASSEUR7QK128
              // objet(s) · … », en un seul paragraphe qui passait à la ligne au
              // milieu d'un identifiant. La règle de mise en colonne vaut
              // désormais pour toutes les listes ; l'ordre, lui, est ici — le nom,
              // ce que la personne possède, puis l'identifiant en filigrane.
              <li key={u.uid} class="row" data-testid={`account-${u.uid}`}>
                <span class="avatar" aria-hidden="true">
                  {initials(u.alias ?? undefined)}
                </span>
                <div class="grow">
                  <span class="card-title" data-testid={`account-alias-${u.uid}`}>
                    {u.alias ?? '—'}
                  </span>
                  {/* Trois morceaux et non une phrase : la règle de pluriel ne
                      connaît qu'un `count` par chaîne, et « 1 objet · 3
                      collections » demande deux accords indépendants. */}
                  <span class="card-meta">
                    {[
                      t('admin.ownsEntities', { count: u.entities }),
                      t('admin.ownsAlbums', { count: u.albums }),
                      formatSize(u.bytes),
                      ...(u.createdAt ? [formatDateTime(u.createdAt)] : []),
                    ].join(' · ')}
                  </span>
                  {/* Le `uid` et rien d'autre. L'adresse n'est stockée sur aucun
                      item (I-7) : la console ne peut pas la montrer, et ce n'est
                      pas un manque — c'est le modèle. */}
                  <span class="row-id mono">{u.uid}</span>
                </div>

                <span class="row-badges">
                  {u.role === 'admin' ? <Pill tone="accent">{t('admin.role.admin')}</Pill> : null}
                  {!u.linked ? <Pill tone="warning">{t('admin.unlinked')}</Pill> : null}
                  {u.status === 'disabled' ? <Pill tone="warning">{t('admin.off')}</Pill> : null}
                </span>

                <PasswordReset uid={u.uid} status={u.status} linked={u.linked} />
                <StatusToggle uid={u.uid} status={u.status} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * **Le courrier qu'on renvoie.**
 *
 * Il manquait, et son absence a coûté un déblocage à la main : un compte créé
 * dont l'invitation se perd est définitivement injoignable, puisque le mot de
 * passe provisoire n'existe que dans ce courrier.
 *
 * Le geste **s'annonce plutôt qu'il ne se confirme** : renvoyer un courrier est
 * réversible — on le renvoie encore —, et faire confirmer un geste inoffensif
 * enseigne à confirmer sans lire. Ce qui compte est *après* : dire lequel des deux
 * courriers est parti, parce qu'on n'y cherche pas la même chose.
 */
function PasswordReset({
  uid,
  status,
  linked,
}: {
  readonly uid: string;
  readonly status: 'active' | 'disabled';
  readonly linked: boolean;
}): JSX.Element | null {
  const [sent, setSent] = useState<'invitation' | 'reset' | undefined>(undefined);
  const moi = me.value?.uid === uid;

  // Trois cas où le serveur refuserait, et où proposer le geste pour le voir
  // échouer serait pire que ne pas le proposer : soi-même (`cannot_change_self`),
  // un espace inerte sans compte Cognito à qui écrire (`not_linked`), et un compte
  // coupé — qu'il faut rouvrir avant, pas réinitialiser.
  if (moi || !linked || status === 'disabled') return null;

  if (sent) {
    return (
      <span class="card-meta" data-testid={`reset-sent-${uid}`}>
        {t(sent === 'invitation' ? 'admin.resetInvitationSent' : 'admin.resetCodeSent')}
      </span>
    );
  }

  return (
    <Action
      testId={`reset-${uid}`}
      variant="quiet"
      onClick={() => void onResetAccountPassword(uid).then(setSent)}
    >
      {t('admin.resetPassword')}
    </Action>
  );
}

/**
 * **Couper un accès se confirme sur place** — pas dans une boîte de dialogue.
 *
 * Le rendre ne se confirme pas : c'est le geste réversible des deux, et demander
 * confirmation pour un geste inoffensif enseigne à confirmer sans lire.
 */
function StatusToggle({
  uid,
  status,
}: {
  readonly uid: string;
  readonly status: 'active' | 'disabled';
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const moi = me.value?.uid === uid;

  // On ne se coupe pas soi-même : le serveur le refuse (`cannot_change_self`), et
  // proposer le geste pour le voir échouer serait pire que ne pas le proposer.
  if (moi) return <span class="card-meta">{t('admin.you')}</span>;

  if (status === 'disabled') {
    return (
      <Action testId={`enable-${uid}`} onClick={() => void onSetAccountStatus(uid, 'active')}>
        {t('admin.enable')}
      </Action>
    );
  }

  return confirming ? (
    <>
      <Action
        testId={`confirm-disable-${uid}`}
        variant="danger"
        onClick={() => {
          setConfirming(false);
          void onSetAccountStatus(uid, 'disabled');
        }}
      >
        {t('admin.confirmDisable')}
      </Action>
      <Action testId={`cancel-disable-${uid}`} variant="quiet" onClick={() => setConfirming(false)}>
        {t('app.cancel')}
      </Action>
    </>
  ) : (
    <Action testId={`disable-${uid}`} variant="quiet" onClick={() => setConfirming(true)}>
      {t('admin.disable')}
    </Action>
  );
}

/**
 * **La création — le geste qui n'existait nulle part.**
 *
 * L'adresse sert à inviter la personne et n'est **pas** conservée (I-7) : elle
 * part chez Cognito, qui envoie le mot de passe temporaire, et la table n'en garde
 * qu'une empreinte. L'écran le dit, sans quoi on chercherait où la relire.
 */
function NewAccount(): JSX.Element {
  const [email, setEmail] = useState('');
  const [alias, setAlias] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');

  const submit = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!email.trim()) return;
    const ok = await onCreateAccount(email.trim(), alias.trim() || undefined, role);
    if (ok) {
      setEmail('');
      setAlias('');
      setRole('user');
    }
  };

  return (
    <section class="panel">
      <h3>{t('admin.newAccount')}</h3>
      {/* **Les champs portent la classe du système.** Ils ne l'avaient pas :
          à côté d'un bouton de 44 px, ils faisaient la hauteur par défaut du
          navigateur, avec sa bordure et son rayon — deux langages dans le même
          formulaire, et la cible tactile perdue sur l'écran qui crée les comptes.
          La feuille de style couvre désormais aussi les éléments nus d'un
          `.form-row`, pour qu'un oubli ne se voie plus. */}
      <form class="form-row" onSubmit={(e) => void submit(e)}>
        <input
          type="email"
          class="input"
          required
          data-testid="account-email"
          placeholder={t('admin.email')}
          aria-label={t('admin.email')}
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
        />
        <input
          type="text"
          class="input"
          data-testid="account-alias"
          placeholder={t('admin.alias')}
          aria-label={t('admin.alias')}
          value={alias}
          onInput={(e) => setAlias((e.target as HTMLInputElement).value)}
        />
        <select
          class="select"
          data-testid="account-role"
          aria-label={t('admin.role')}
          value={role}
          onChange={(e) => setRole((e.target as HTMLSelectElement).value as 'user' | 'admin')}
        >
          <option value="user">{t('admin.role.user')}</option>
          <option value="admin">{t('admin.role.admin')}</option>
        </select>
        {/* `type="submit"` et non un `Action` : c'est la touche Entrée depuis le
            champ d'adresse qui doit valider, et `Action` monte des boutons
            `type="button"` — délibérément, puisque partout ailleurs il n'y a pas
            de formulaire. */}
        {/* C'est **l'action de ce panneau** : elle en porte donc la teinte. Le
            seul bouton primaire de l'écran était jusqu'ici le « + Ajouter » de la
            barre, qui n'ajoute rien ici — il n'y est plus. */}
        <button type="submit" class="btn primary" data-testid="account-submit">
          {t('admin.create')}
        </button>
      </form>
      <p class="card-meta">{t('admin.emailNotStored')}</p>
    </section>
  );
}
